import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { CaseDiscountType, CreateOrderDiscountInput, Deduction } from 'API';
import { DialogBox } from 'components/common/Dialog/DialogBox';
import InputError from 'components/common/InputError';
import AddSpecialDiscountInput from 'components/SpecialDiscount/SpecialDiscount/AddSpecialDiscount/AddSpecialDiscount';
import { AddSpecialDiscountModal } from 'components/SpecialDiscount/SpecialDiscount/AddSpecialDiscountModal/AddSpecialDiscountModal';
import { DiscountModalType } from 'components/SpecialDiscount/SpecialDiscount/AddSpecialDiscountModal/types';
import { useAuth } from 'providers/AuthProvider';
import { OrderModuleActionsContext, OrderModuleContext } from 'providers/OrderModuleProvider';
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DISCOUNT_TYPE_LABELS } from 'shared/constants/invoice-data.constants';
import { DiscountForType } from 'shared/enums';
import { getNewDiscountInput } from 'shared/helpers/discount.helper';
import { getAppliedSpecialDiscountItem } from 'shared/helpers/invoice/invoice.helper';
import { convertLocalOrderInputToOrder } from 'shared/helpers/order-detail/order-detail.helper';
import {
  convertDiscountInputToDeduction,
  getAddOnDescriptionString,
  isDescriptionAddOnOrService,
} from 'shared/helpers/order-entry/order-entry.helper';
import { useAddDiscountModalStore } from 'stores/useAddDiscountModalStore';
import { AddedSpecialDiscountItemType } from 'types/common';

/**
 * Props interface for the OrderEntryAddSpecialDiscount component.
 */
interface OrderEntryAddSpecialDiscountProps {
  isLoading: boolean;
}

/**
 * Component for managing special discounts in order entry.
 * Allows adding, displaying, and removing special discounts.
 */
export const OrderEntryAddSpecialDiscount: FC<OrderEntryAddSpecialDiscountProps> = ({ isLoading }) => {
  const { order } = useContext(OrderModuleContext);
  const { user } = useAuth();
  const { patchOrder } = useContext(OrderModuleActionsContext);
  const specialDiscount = useAddDiscountModalStore(state => state.specialDiscount);
  const setCurrentOrder = useAddDiscountModalStore(state => state.setCurrentOrder);

  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [addedSpecialDiscounts, setAddedDiscounts] = useState<AddedSpecialDiscountItemType[]>([]);
  const [appliedDiscounts, setAppliedDiscounts] = useState<Deduction[]>([]);
  const [removingIndex, setRemovingIndex] = useState<number | undefined>(undefined);
  const orderDiscounts = useMemo(() => order.discounts, [order.discounts]);

  // Clear error message when provider is selected
  useEffect(() => {
    if (order?.providerName) {
      setErrorMessage('');
    }
  }, [order?.providerName]);

  /**
   * Update current order
   */
  const setOrderInputAsCurrentOrder = useCallback(() => {
    if (order) {
      const createdOrder = convertLocalOrderInputToOrder(order);
      setCurrentOrder(createdOrder);
    }
  }, [order, setCurrentOrder]);

  // Update current order in the store when order changes
  useEffect(() => {
    setOrderInputAsCurrentOrder();
  }, [order, setOrderInputAsCurrentOrder]);

  /**
   * Opens the add special discount modal if a provider is selected.
   */
  const onOpenModal = useCallback(() => {
    if (!order?.providerName) {
      setErrorMessage('Please select a provider');

      return;
    }
    setOrderInputAsCurrentOrder();
    setShowModal(true);
  }, [order?.providerName, setOrderInputAsCurrentOrder]);

  /**
   * Closes the add special discount modal and clears the current order.
   */
  const onCloseModal = useCallback(() => {
    setCurrentOrder(null);
    setShowModal(false);
  }, [setCurrentOrder]);

  /**
   * Applies a discount to the order and updates the state.
   */
  const onDiscountApply = useCallback(() => {
    const { selectedDiscountType, discounts: selectedDiscounts, note, reason: selectedReason } = specialDiscount;

    const orderNumber = order?.orderNumber || '';
    const displayName = user?.displayName;

    const newDiscountInput = getNewDiscountInput(
      displayName,
      selectedDiscountType,
      selectedDiscounts,
      note,
      orderNumber,
      selectedReason
    );

    const convertedDiscount = convertDiscountInputToDeduction(newDiscountInput);
    const isPartialCase = selectedDiscountType && selectedDiscountType !== CaseDiscountType.EntireCase;

    /**
     * Normalizes appliedDiscounts to ensure each deduction is separated
     * when handling part of cases for easier mapping to orderDiscounts.
     */
    const discounts = isPartialCase
      ? convertedDiscount.deductions.map(deduction => ({
          ...convertedDiscount,
          deductions: [deduction],
        }))
      : [convertedDiscount];

    const newAppliedDiscounts = [...appliedDiscounts, ...discounts];
    const addedDiscount = getAppliedSpecialDiscountItem([convertedDiscount], '');
    setAppliedDiscounts(newAppliedDiscounts);
    setAddedDiscounts(prev => [...prev, ...addedDiscount]);
    const updatedOrderDiscounts: CreateOrderDiscountInput[] = newAppliedDiscounts.map(
      ({ type: discountType, notes, deductions, reason }) => ({
        type: discountType,
        notes,
        deductions: deductions.map(
          ({ description, discountAmount, discountType, orderItemId, productCode, value }) => ({
            description: description || '',
            discountAmount,
            discountType,
            orderItemId: orderItemId || '',
            productCode,
            value,
          })
        ),
        reason,
      })
    );
    patchOrder({
      discounts: updatedOrderDiscounts,
    });
    setShowModal(false);
  }, [specialDiscount, user?.displayName, order?.orderNumber, appliedDiscounts, patchOrder]);

  /**
   * Removes the target discounts from the order object.
   * In LMS1-9120, we established that we should also verify the description field of the discount.
   * This is to ensure that for a single product with multiple teeth, only the target tooth gets removed.
   * @param discountsToRemove - the discounts to remove from this order.
   */
  const removeDiscountInOrder = useCallback(
    (discountsToRemove: AddedSpecialDiscountItemType[]) => {
      if (discountsToRemove.some(orderItem => orderItem.orderItemId === DiscountForType.EntireCase)) {
        patchOrder({ discounts: undefined });
      } else if (orderDiscounts) {
        const updatedDeductions = orderDiscounts?.filter(
          item =>
            !item?.deductions.some(deduction =>
              discountsToRemove.some(
                discountToRemove =>
                  discountToRemove.orderItemId === deduction.orderItemId &&
                  discountToRemove.description === deduction.description
              )
            )
        );
        patchOrder({
          discounts: updatedDeductions,
        });
      }
    },
    [orderDiscounts, patchOrder]
  );

  /**
   * Removes a special discount at the specified index.
   */
  const removeSpecialDiscount = useCallback(() => {
    if (removingIndex !== undefined) {
      setAddedDiscounts(prevDiscounts => {
        const newDiscounts = [...prevDiscounts];
        newDiscounts.splice(removingIndex, 1);
        return newDiscounts;
      });

      setAppliedDiscounts(prevDiscounts => {
        const newDiscounts = [...prevDiscounts];
        newDiscounts.splice(removingIndex, 1);
        return newDiscounts;
      });
      const discountToRemove = addedSpecialDiscounts[removingIndex];
      removeDiscountInOrder([discountToRemove]);
    }

    setShowConfirmModal(false);
  }, [removingIndex, addedSpecialDiscounts, removeDiscountInOrder]);

  /**
   * Initiates the discount removal process.
   */
  const handleDeleteItem = useCallback((_: string, __: string, index: number) => {
    setShowConfirmModal(true);
    setRemovingIndex(index);
  }, []);

  const discountDescription = useCallback(
    (orderItemId: string, description: string) => {
      const targetAddOn = order.orderItems
        .find(item => item.id === orderItemId)
        ?.addOns.find(addOn => description?.startsWith(`${addOn.name} (${addOn.type})`));
      return targetAddOn ? getAddOnDescriptionString(targetAddOn) : null;
    },
    [order]
  );

  // syncs all discounts with the new description if there is a change in add-on quantity
  // refer https://glidewell.atlassian.net/browse/LMS1-8832
  useEffect(() => {
    setAddedDiscounts(prevDiscounts =>
      prevDiscounts
        .map(disc => {
          const { orderItemId, description } = disc;
          if (!orderItemId || !description) return disc;

          const newDescription = discountDescription(orderItemId, description);

          // Handles removing add-ons and services with no matching description.
          const isAddOnOrServiceRemoved = !newDescription && isDescriptionAddOnOrService(description);
          if (isAddOnOrServiceRemoved) {
            return {
              ...disc,
              description: '',
            };
          }

          return {
            ...disc,
            ...(!!newDescription && { description: newDescription }),
          };
        })
        .filter(discount => !!discount.description)
    );

    setAppliedDiscounts(prevDiscounts => {
      const newDiscounts = prevDiscounts
        .map(disc => {
          const { deductions } = disc;
          const newDeductions = deductions
            .map(deduction => {
              const { orderItemId, description } = deduction;
              if (!orderItemId || !description) return deduction;

              const newDescription = discountDescription(orderItemId, description);

              // Handles removing add-ons and services with no matching description.
              const isAddOnOrServiceRemoved = !newDescription && isDescriptionAddOnOrService(description);
              if (isAddOnOrServiceRemoved) {
                return {
                  ...deduction,
                  description: '',
                };
              }

              return {
                ...deduction,
                ...(!!newDescription && { description: newDescription }),
              };
            })
            .filter(deduction => !!deduction.description);

          return {
            ...disc,
            deductions: newDeductions,
          };
        })
        .filter(discount => discount.deductions.length > 0);

      return newDiscounts;
    });
  }, [order.orderItems, discountDescription]);

  /**
   * Handles removing discounts for any products that get removed from an order, before it gets created.
   * Please see the comments under LMS1-8769 for more information.
   */
  useEffect(() => {
    const orderItemIds = order.orderItems.map(orderItem => orderItem.itemId || orderItem.id);
    const orderItemIdsToRemove: string[] = [];
    const orderItemsToRemove: AddedSpecialDiscountItemType[] = [];

    addedSpecialDiscounts.forEach((orderItem: AddedSpecialDiscountItemType) => {
      const { orderItemId } = orderItem;
      const isCaseLevelDiscount = Object.values<string>(DISCOUNT_TYPE_LABELS).includes(orderItemId);
      if (!isCaseLevelDiscount && !orderItemIds.includes(orderItemId)) {
        orderItemIdsToRemove.push(orderItemId);
        orderItemsToRemove.push(orderItem);
      }
    });

    if (orderItemIdsToRemove.length > 0) {
      setAddedDiscounts(prevDiscounts => {
        return [...prevDiscounts].filter(discount => !orderItemIdsToRemove.includes(discount.orderItemId));
      });

      setAppliedDiscounts(prevDiscounts => {
        return [...prevDiscounts].filter(
          discount => !orderItemIdsToRemove.includes(discount?.deductions[0]?.orderItemId || '')
        );
      });

      if (orderDiscounts) {
        removeDiscountInOrder(orderItemsToRemove);
      }
    }
  }, [
    addedSpecialDiscounts,
    appliedDiscounts,
    orderDiscounts,
    order.orderItems,
    patchOrder,
    removeDiscountInOrder,
    removeSpecialDiscount,
  ]);

  // Memoize the confirm dialog to prevent unnecessary re-renders
  const confirmDialog = useMemo(
    () =>
      showConfirmModal && (
        <DialogBox
          title="Are you sure you want to remove this discount?"
          onCancel={() => setShowConfirmModal(false)}
          icon={<ExclamationTriangleIcon className="h-6 w-6 text-red-600" />}
          confirmText="Remove"
          onConfirm={removeSpecialDiscount}
        >
          <div className="text-base text-gray-500"></div>
        </DialogBox>
      ),
    [showConfirmModal, removeSpecialDiscount]
  );

  return (
    <div className="flex flex-col gap-2">
      <AddSpecialDiscountInput
        onAddButtonClick={onOpenModal}
        isLoading={isLoading}
        addedSpecialDiscountsItems={addedSpecialDiscounts}
        onDeleteItem={handleDeleteItem}
        hideTitle
        isShowAsPending
      />
      <InputError message={errorMessage} testId="coupon" />
      <AddSpecialDiscountModal
        showModal={showModal}
        onClose={onCloseModal}
        onApply={onDiscountApply}
        type={DiscountModalType.SpecialDiscount}
        caseEntryAppliedDiscounts={appliedDiscounts}
      />
      {confirmDialog}
    </div>
  );
};
